import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { ALLOWED_FILE_TYPES } from '../../core/constants';
import { NotificationService } from '../../services/notification.service';

@Component({
    selector: 'app-upload-p12-modal',
    templateUrl: './upload-p12-modal.component.html',
    styleUrl: './upload-p12-modal.component.css',
    standalone: false
})
export class UploadP12ModalComponent {
  selectedFile: File | null = null;
  password: string = '';
  passwordConfirm: string = '';
  passwordVisible = false;
  confirmPasswordVisible = false;
  isLoading: boolean = false;
  /** Resaltado al arrastrar archivo sobre la zona */
  isDragActive = false;
  private dragCounter = 0;
  @Output() certificateEntered = new EventEmitter<{ selectedFile: File; password: string }>();

  get canSubmit(): boolean {
    return !!(
      this.selectedFile &&
      this.password &&
      this.passwordConfirm &&
      this.password === this.passwordConfirm
    );
  }


  constructor(
    public dialogRef: MatDialogRef<UploadP12ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Record<string, unknown>,
    private notificationService: NotificationService
  ) { }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragActive = false;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDragActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.tryAcceptFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }
    this.tryAcceptFile(file);
  }

  private tryAcceptFile(file: File): void {
    const isValidExtension = ALLOWED_FILE_TYPES.P10.extensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (isValidExtension) {
      this.selectedFile = file;
      return;
    }

    Swal.fire({
      ...this.notificationService.getSwalBaseConfig(),
      icon: 'error',
      title: 'Certificado inválido',
      text: `Por favor, seleccione un certificado PKCS10 (${ALLOWED_FILE_TYPES.P10.extensions.join(', ')}) válido.`,
      confirmButtonText: 'Entendido',
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        confirmButton: 'custom-swal-button'
      }
    });
  }

  clearFile(input?: HTMLInputElement): void {
    this.selectedFile = null;
    if (input) {
      input.value = '';
    }
  }

  onSubmit(): void {
    if (!this.selectedFile || !this.password || !this.passwordConfirm) {
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'error',
        title: 'Campos obligatorios',
        text: 'Seleccione el certificado e ingrese y confirme la contraseña.',
        confirmButtonText: 'Entendido',
        customClass: {
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          confirmButton: 'custom-swal-button'
        }
      });
      return;
    }
    if (this.password !== this.passwordConfirm) {
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'error',
        title: 'Contraseñas no coinciden',
        text: 'La contraseña y la confirmación deben ser iguales.',
        confirmButtonText: 'Entendido',
        customClass: {
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          confirmButton: 'custom-swal-button'
        }
      });
      return;
    }

    this.isLoading = true;
    this.dialogRef.close({
      certificateFile: this.selectedFile,
      password: this.password
    });
    this.certificateEntered.emit({ selectedFile: this.selectedFile, password: this.password });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

}
